import { useMemo } from 'react';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import { getMonthToDateRange } from '@/utils/date';
import { DATE_RANGE_FILTER_KEYS } from '@/utils/filterFields';

type Search = Record<string, unknown>;

const toFilterValues = (search: Search): Record<string, string> =>
  Object.fromEntries(
    Object.entries(search)
      .filter(([, value]) => value !== undefined && value !== '')
      .map(([key, value]) => [key, String(value)]),
  );

/**
 * A filter form's values are all strings; the route schemas want numbers and
 * booleans back, so each key named in numericKeys is coerced on the way out and
 * 'true'/'false' become real booleans. An emptied field becomes undefined so it
 * drops off the URL rather than lingering as an empty parameter.
 */
export function toRequisitionSearch(
  values: Record<string, string>,
  numericKeys: readonly string[],
): Search {
  const next: Search = {};

  for (const [key, value] of Object.entries(values)) {
    if (value === '') next[key] = undefined;
    else if (numericKeys.includes(key)) next[key] = Number(value);
    else if (value === 'true' || value === 'false') next[key] = value === 'true';
    else next[key] = value;
  }

  return next;
}

interface RequisitionFilterOptions {
  search: Search;
  onApplyFilters: (next: Search) => void;
  onApplySearch?: ((text: string) => void) | undefined;
  numericKeys: readonly string[];
  withDateRange?: boolean;
}

/**
 * The shared filter controller for the Product Requisition screens. Each one
 * keeps its filters in the URL so a filtered report can be linked and reloaded,
 * the way the legacy screens kept theirs on the query string. The page owns the
 * navigation, since a route's navigate is typed to that route.
 */
export function useRequisitionFilters({
  search,
  onApplyFilters,
  onApplySearch,
  numericKeys,
  withDateRange = false,
}: RequisitionFilterOptions) {
  const defaultPeriod = useMemo(
    () => (withDateRange ? getMonthToDateRange() : {}),
    [withDateRange],
  );
  const appliedValues = useMemo(() => toFilterValues(search), [search]);

  const filters = useUrlSyncedFilters({
    appliedValues,
    shouldWaitForApply: true,
    defaultValues: defaultPeriod,
    rangeKeys: withDateRange ? DATE_RANGE_FILTER_KEYS : [],
    onApply: (values) => onApplyFilters(toRequisitionSearch(values, numericKeys)),
  });

  const listSearch = useUrlSyncedSearch({
    appliedText: (search.search as string | undefined) ?? '',
    onApply: (text) => onApplySearch?.(text ?? ''),
  });

  // A key the schema left as undefined must not overwrite the default period,
  // so the defaults go on first and only the keys that carry a value go over.
  const params = useMemo(() => {
    const set = Object.fromEntries(
      Object.entries(search).filter(([, value]) => value !== undefined),
    );
    return { ...defaultPeriod, ...set };
  }, [search, defaultPeriod]);

  return { ...filters, ...listSearch, params };
}
